import { useEffect, useRef, useState } from 'react';
import {
    MAX_VENDOR_NAME_LENGTH,
    type UpdateVendorBody,
    type VendorDetail
} from '../../../services/vendors';

export interface VendorEditFormState {
    name: string;
    setName: (name: string) => void;
    nameValid: boolean;
    isDirty: boolean;
    changes: UpdateVendorBody;
}

// Callable before the drawer's loading/error early returns, so vendor may be
// undefined; the reseed guard below still fires once the fetch resolves.
export function useVendorEditForm(vendor: VendorDetail | undefined): VendorEditFormState {
    const [name, setName] = useState(vendor?.name ?? '');

    // Keyed on updatedAt, not object identity: a refetch or reconnect hands back
    // an equal-but-new object and must not wipe unsaved edits.
    const seededAt = useRef(vendor?.updatedAt);
    useEffect(() => {
        if (!vendor || seededAt.current === vendor.updatedAt) return;
        seededAt.current = vendor.updatedAt;
        setName(vendor.name);
    }, [vendor]);

    const trimmedName = name.trim();
    const nameValid = trimmedName.length > 0 && trimmedName.length <= MAX_VENDOR_NAME_LENGTH;
    const changes: UpdateVendorBody = {
        ...(vendor && trimmedName !== vendor.name && { name: trimmedName })
    };
    const isDirty = Object.keys(changes).length > 0;

    return { name, setName, nameValid, isDirty, changes };
}
